import './style.scss';
import { PurchaseSimulationInput } from '../hooks';
import { DomesticRealEstateFullInfo } from 'views/pages/symbol/module/fullInfo';
import { DomesticRealEstateThumbnail } from 'views/pages/symbol/module/thumbnail';
import { DomesticRealEstateBasicInfo } from 'views/pages/symbol/module/basicInfo';
import { Description, labelColor, labelSize } from 'views/mutual/description';
import { Button, ButtonFill, ButtonSize, ButtonType } from 'views/mutual/button';
import { FormikProps } from 'formik';
import { useCustomLocationName } from 'views/mutual/location/hooks';
import { LotInput_ItExplanationDomesticRealEstate } from 'views/pages/symbol/module/lotInput';
import { icons } from 'constants/icons';
import { IconColor, IconSize, SvgIcon } from 'views/mutual/svgIcon';
import { MessageModal } from 'views/mutual/modal/messageModal';
import { ModalSpacing } from 'views/mutual/modal';
import { useIsMobile } from 'views/utils/checkWidth';
import { DomesticRealEstateSymbolDetailInfo } from 'views/utils/graphql/graphql';

type DomesticRealEstateItExplanationSymbolDetailProps = {
  symbolDetail: DomesticRealEstateSymbolDetailInfo;
  formik: FormikProps<PurchaseSimulationInput>;
  canApplyItExplanation: boolean;
  lotInputControlProps: {
    disabled: boolean | undefined;
    error: string | undefined;
    value: number | null | undefined;
    onChange: ((value: string) => void) | undefined;
  };
  modalProps: any;
};

export const DomesticRealEstateItExplanationSymbolDetail = ({
  symbolDetail,
  formik,
  canApplyItExplanation,
  lotInputControlProps,
  modalProps,
}: DomesticRealEstateItExplanationSymbolDetailProps) => {
  const { isMobile } = useIsMobile();
  useCustomLocationName(symbolDetail.ProductName!);

  return (
    <form onSubmit={formik.handleSubmit} className="domesticRealEstateItExplanation">
      {!canApplyItExplanation && (
        <div className="disableApplyDescription">
          <Description
            label="こちらの案件は現在お申し込みいただけません"
            labelColor={labelColor.red}
            labelSize={labelSize.large}
          />
        </div>
      )}
      <div className="programDetail">
        <div className="programDetail_card">
          <DomesticRealEstateThumbnail {...symbolDetail} />
          <DomesticRealEstateBasicInfo isFull={true} {...symbolDetail} />
          <DomesticRealEstateFullInfo {...symbolDetail!} />
          <div className="description">
            <Description
              labelColor={labelColor.gray}
              labelSize={labelSize.small}
              label="※　電子交付・IT重説のお申込みをいただいた場合でも、ご状況によっては、お申込みを承れない場合があります。予めご了承ください。"
            />
          </div>
          <div className="programDetail_card_wrapper">
            <a
              className="documentLink"
              href={symbolDetail.DetailURL ?? ''}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                formik.setFieldValue('isContractDocumentOpened', true);
              }}
            >
              <Button buttonFill={ButtonFill.white} buttonSize={ButtonSize.small}>
                <div className="documentLink_content">
                  <SvgIcon icon={icons.file} color={IconColor.blue} size={IconSize.icon_xs} />
                  <span className="documentLink_content_label">物件詳細資料</span>
                </div>
              </Button>
            </a>
            <a
              className="documentLink"
              href={symbolDetail.InheritanceTaxAssessmentURL?.trim() ? symbolDetail.InheritanceTaxAssessmentURL : undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button buttonFill={ButtonFill.white} buttonSize={ButtonSize.small} disable={!symbolDetail.InheritanceTaxAssessmentURL}>
                <div className="documentLink_content">
                  <SvgIcon icon={icons.file} color={IconColor.blue} size={IconSize.icon_xs} />
                  <span className="documentLink_content_label">相続税評価資料</span>
                </div>
              </Button>
            </a>
          </div>
          <div className="domesticRealEstateItExplanation_label">
            <span>
              {
                '本信託受益権の売買には、法令により、宅地建物取引士がお客様に対して重要事項説明書を交付して説明をする必要があります。このFPGオンラインサービスによる取引では、重要事項説明書を電子交付させていただくとともに、ご説明はウェブ会議システムを使用して行わせていただきます（このウェブ会議システムを使用した重要事項説明を「IT重説」と呼びます）。もしこれらと異なるお取引方法をご希望される場合には、画面上部の「お問い合わせ」からお申し出ください。'
              }
            </span>
            <br />
            <span>
              {
                '重要事項説明書の電子交付とIT重説の実施をご希望される場合には、これらの手続きについてご承諾いただくとともに、重要事項説明書を電子交付するため、お届けいただいたメールアドレス（指定アドレス）宛に電子メールを送信させていただきます。'
              }
            </span>
          </div>
          <MessageModal {...modalProps} spacing={ModalSpacing.small} shouldCloseOnOverlayClick={false} />
          {/* スマホ画面用入力フォーム */}
          {isMobile && (
            <div className="programDetail_request_mobile">
              <LotInput_ItExplanationDomesticRealEstate
                title="申込個数"
                realEstateBeneficiaryRightPrice={symbolDetail.RealEstateBeneficiaryRightPrice!}
                realEstatePriceEquivalent={symbolDetail.RealEstatePriceEquivalent!}
                consumptionTaxAmount={symbolDetail.ConsumptionTaxAmount!}
                repairReserveFundEquivalent={symbolDetail.RepairReserveFundEquivalent!}
                requiredWorkingCapitalReserveEquivalent={symbolDetail.RequiredWorkingCapitalReserveEquivalent!}
                disabled={lotInputControlProps.disabled}
                error={lotInputControlProps.error}
                value={lotInputControlProps.value}
                onChange={lotInputControlProps.onChange}
                symbolDetail={symbolDetail}
                showNotice={true}
              />
            </div>
          )}
          <div className="domesticRealEstateItExplanation_button">
            <Button
              type={ButtonType.Submit}
              label="電子交付・IT重説の申込みに進む"
              buttonFill={ButtonFill.black}
              buttonSize={ButtonSize.medium}
              disable={!canApplyItExplanation}
            />
          </div>
        </div>
        {/* PC画面用入力フォーム */}
        {!isMobile && (
          <div className="programDetail_request_pc">
            <LotInput_ItExplanationDomesticRealEstate
              title="申込個数"
              realEstateBeneficiaryRightPrice={symbolDetail.RealEstateBeneficiaryRightPrice!}
              realEstatePriceEquivalent={symbolDetail.RealEstatePriceEquivalent!}
              consumptionTaxAmount={symbolDetail.ConsumptionTaxAmount!}
              repairReserveFundEquivalent={symbolDetail.RepairReserveFundEquivalent!}
              requiredWorkingCapitalReserveEquivalent={symbolDetail.RequiredWorkingCapitalReserveEquivalent!}
              disabled={lotInputControlProps.disabled}
              error={lotInputControlProps.error}
              value={lotInputControlProps.value}
              onChange={lotInputControlProps.onChange}
              symbolDetail={symbolDetail}
              showNotice={true}
            />
          </div>
        )}
      </div>
    </form>
  );
};
